import React, { useId, useState } from 'react';
import styled from 'styled-components';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

/**
 * The "How we measure these" disclosure at the foot of a dark band (ProofBand on managed IT,
 * SafetyBand on the AI page).
 *
 * The panel stays in the DOM when closed, hidden with `visibility` (the same pattern as the
 * FAQ answers), so the text is crawlable and is not reached by Tab or a screen reader until
 * it is opened. Beach on Midnight is 7.6:1.
 */
interface BandDisclosureProps {
  label: string;
  children: React.ReactNode;
}

const Disclosure = styled.div`
  margin-top: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.14);
`;

const Toggle = styled.button`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  width: 100%;
  padding: 9px 0;
  border: 0;
  background: none;
  color: var(--davinci-beach);
  font: inherit;
  font-size: var(--fs-sm);
  font-weight: var(--font-weight-bold);
  text-align: left;
  cursor: pointer;

  svg {
    width: 18px;
    height: 18px;
    transition: transform var(--transition-base);
  }

  &[aria-expanded='true'] svg {
    transform: rotate(180deg);
  }

  &:focus-visible {
    outline: 3px solid var(--davinci-beach);
    outline-offset: 2px;
  }
`;

const Collapse = styled.div<{ $open: boolean }>`
  display: grid;
  grid-template-rows: ${({ $open }) => ($open ? '1fr' : '0fr')};
  /* The text stays in the DOM for crawlers. Collapsed, it is hidden from Tab and
     from screen readers once the transition has finished. */
  visibility: ${({ $open }) => ($open ? 'visible' : 'hidden')};
  transition: grid-template-rows 0.3s ease,
    visibility 0s linear ${({ $open }) => ($open ? '0s' : '0.3s')};

  > div {
    min-height: 0;
    overflow: hidden;
  }
`;

const BandDisclosure: React.FC<BandDisclosureProps> = ({ label, children }) => {
  const [open, setOpen] = useState(false);
  const panelId = `${useId()}-definitions`;

  return (
    <Disclosure>
      <Toggle
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((current) => !current)}
      >
        {label}
        <ChevronDownIcon aria-hidden="true" />
      </Toggle>
      <Collapse id={panelId} $open={open}>
        <div>{children}</div>
      </Collapse>
    </Disclosure>
  );
};

export default BandDisclosure;
